import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import {
	JobWithShare,
	SupabaseShareEntityViewEnum,
} from "@/lib/supabase/supabaseTypes";
import { JobStatusEnum } from "@/lib/types/job";
import { Logger } from "@/lib/logger/Logger";
import { VendorJobsTable } from "./vendor-jobs-table.component";
import { usePatientStore } from "../../store/Patient";

// TODO: Improve page generally including db fetches

const VERRECHNET_STATUSES = [
	JobStatusEnum.PAID_SINGLE,
	JobStatusEnum.PAID_MONTHLY,
	JobStatusEnum.BOOKED_SINGLE,
	JobStatusEnum.BOOKED_MONTHLY,
];

interface VendorVerrechnetPageProps {
	organizationId: string;
}

export const VendorVerrechnetPage: React.FC<VendorVerrechnetPageProps> = ({
	organizationId,
}) => {
	const { vendorId } = useParams<{ vendorId: string }>();
	const patientNameFn = usePatientStore((state) => state.patientName);
	const [jobs, setJobs] = useState<JobWithShare[]>([]);
	const [patientNames, setPatientNames] = useState<Record<string, string>>(
		{}
	);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchVerrechnetJobs = async () => {
			if (!vendorId || !organizationId) return;

			setLoading(true);

			const twelveMonthsAgo = new Date();
			twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);

			const { data, error } = await supabase
				.from(SupabaseShareEntityViewEnum.JOBS_WITH_SHARES)
				.select("*")
				.eq("organization_id", vendorId)
				.eq("shared_with", organizationId)
				.in("status", VERRECHNET_STATUSES)
				.lt("created_at", twelveMonthsAgo.toISOString())
				.order("created_at", { ascending: false });

			if (error) {
				Logger.error(
					error,
					{},
					"Error fetching vendor verrechnet jobs"
				);
				setJobs([]);
				setLoading(false);
				return;
			}

			const jobsData = (data as JobWithShare[]) ?? [];
			setJobs(jobsData);

			const patientIds = [
				...new Set(
					jobsData
						.map((j) => j.patient_id)
						.filter(Boolean) as string[]
				),
			];
			if (patientIds.length > 0) {
				const { data: patients } = await supabase
					.from("patients")
					.select("id, first_name, last_name")
					.in("id", patientIds);

				if (patients) {
					const lookup: Record<string, string> = {};
					for (const p of patients) {
						lookup[p.id] = patientNameFn({
							patient_id: p.id,
							first_name: p.first_name ?? "",
							last_name: p.last_name ?? "",
							code: "",
							title: "",
						});
					}
					setPatientNames(lookup);
				}
			}

			setLoading(false);
		};

		fetchVerrechnetJobs();
	}, [vendorId, organizationId]);

	return (
		<div className="h-full p-4">
			<h2 className="text-lg font-semibold mb-4">Verrechnet</h2>
			<div className="h-[calc(100%-3rem)]">
				<VendorJobsTable
					jobs={jobs}
					vendorOrgId={vendorId ?? ""}
					loading={loading}
					patientNames={patientNames}
				/>
			</div>
		</div>
	);
};
